import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine,
} from "recharts";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { ACCENT, ECOG_COLOR, fmtDate, Field, HistoryTable } from "./shared";

export default function PerfTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    mode: "ecog",
    ecog: 1,
    karnofsky: 80,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/oncology/performance", {
        date: form.date,
        ecog: form.mode === "ecog" ? Number(form.ecog) : null,
        karnofsky: form.mode === "karnofsky" ? Number(form.karnofsky) : null,
        notes: form.notes || null,
      });
      toast.success("Performance status recorded");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save performance status");
    } finally { setSaving(false); }
  }

  const chartData = useMemo(
    () => [...data.records].slice().reverse().map((r) => ({
      date: fmtDate(r.date),
      ECOG: r.ecog,
    })),
    [data.records]
  );

  return (
    <div className="space-y-6" data-testid="onco-perf-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="perf-form">
        <div className="font-heading text-lg">Record performance status</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="perf-input-date" className="ros-input" required />
          </Field>
          <Field label="Scale">
            <select value={form.mode}
                    onChange={(e) => setForm({ ...form, mode: e.target.value })}
                    data-testid="perf-input-mode" className="ros-input">
              <option value="ecog">ECOG (0–5)</option>
              <option value="karnofsky">Karnofsky (0–100)</option>
            </select>
          </Field>
          {form.mode === "ecog" ? (
            <Field label="ECOG score">
              <select value={form.ecog}
                      onChange={(e) => setForm({ ...form, ecog: e.target.value })}
                      data-testid="perf-input-ecog" className="ros-input">
                <option value={0}>0 — Fully active</option>
                <option value={1}>1 — Restricted strenuous activity</option>
                <option value={2}>2 — Ambulatory, cannot work</option>
                <option value={3}>3 — Limited self-care</option>
                <option value={4}>4 — Completely disabled</option>
                <option value={5}>5 — Deceased</option>
              </select>
            </Field>
          ) : (
            <Field label="Karnofsky score">
              <input type="number" min={0} max={100} step={10} value={form.karnofsky}
                     onChange={(e) => setForm({ ...form, karnofsky: e.target.value })}
                     data-testid="perf-input-karnofsky" className="ros-input" />
            </Field>
          )}
        </div>

        <Field label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="perf-input-notes"
                    className="ros-input" />
        </Field>

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="perf-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="perf-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Save assessment"}
          </button>
        </div>
      </form>

      {chartData.length > 0 && (
        <div className="ros-card" data-testid="perf-chart">
          <div className="font-heading text-lg mb-3">ECOG trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} domain={[0, 5]} reversed />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <ReferenceLine y={2} stroke="#FBBF24" strokeDasharray="4 4"
                               label={{ value: "Trial threshold ≤2", fontSize: 10, fill: "#FBBF24" }} />
                <Line type="monotone" dataKey="ECOG" stroke={ACCENT} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`ECOG history (${data.records.length})`}
        testId="perf-history"
        rows={data.records}
        rowTestId={(r) => `perf-row-${r.id}`}
        empty="No performance status assessments."
        columns={[
          { label: "Date",  render: (r) => fmtDate(r.date) },
          { label: "ECOG",  render: (r) => (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ color: ECOG_COLOR[r.ecog], background: `${ECOG_COLOR[r.ecog]}18`,
                           border: `1px solid ${ECOG_COLOR[r.ecog]}40` }}>
              {r.ecog}
            </span>
          )},
          { label: "Karnofsky", render: (r) => <span className="font-mono text-[#A3ACA7]">{r.karnofsky ?? "—"}</span> },
          { label: "Label", render: (r) => <span className="text-xs">{r.label}</span> },
          { label: "Recommendation", render: (r) => <span className="text-xs text-[#A3ACA7]">{r.recommendation}</span> },
        ]}
      />
    </div>
  );
}
